/**
 * +zone: stylised water for terrain maps (content `fieldTerrainOf(mapId).water`): the lake (a flat
 * sheet over the basin), the river (a ribbon that follows its bed downhill) and the waterfall (a
 * falling sheet with foam and mist at its foot). One shader: depth tint (pale turquoise shallows,
 * deep blue middle), soft animated ripples, a foam line where the water meets the shore, sun
 * glints. Transparent, no depth write; ~3 draws.
 */
import * as THREE from 'three';
import { NOISE_GLSL, U } from '../style';
import { motes } from './glow';

type Water = {
  level: number;
  lake: { c: { x: number; y: number }; rx: number; ry: number };
  river: readonly { x: number; y: number }[];
  riverHalf: number;
  falls: { x: number; y: number; top: number };
};

export interface WaterView {
  group: THREE.Group;
  dispose(): void;
}

const VERT = `attribute float aDepth; varying float vDepth; varying vec3 vW;
  void main(){ vDepth = aDepth; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;

const FRAG = `uniform float uTime; uniform vec3 uFogColor; uniform float uFogNear; uniform float uFogFar; uniform vec4 uFogShape; uniform vec3 uShallow; uniform vec3 uDeep; uniform vec3 uFoam; uniform float uFlow;
  uniform vec3 uSkyLow; uniform vec3 uSkyHigh; uniform vec3 uSunDir;
  varying float vDepth; varying vec3 vW;
  ${NOISE_GLSL}
  void main(){
    float d = max(vDepth, 0.0);
    vec2 p = vW.xz * 0.18 + vec2(uTime * 0.03, uTime * 0.02 + uFlow * uTime * 0.25);
    float n = noise(p) * 0.6 + noise(p * 2.7 - uTime * 0.05) * 0.4;
    vec3 c = mix(uShallow, uDeep, smoothstep(0.1, 2.4, d));
    // Ripple bands and sun glints.
    float rip = smoothstep(0.58, 0.62, n) - smoothstep(0.66, 0.72, n);
    c += vec3(0.55, 0.7, 0.75) * rip * 0.22;
    float glint = smoothstep(0.82, 0.9, noise(vW.xz * 0.9 + vec2(uTime * 0.2, -uTime * 0.13)));
    c += vec3(1.0, 0.95, 0.85) * glint * 0.35;
    // +scenery: fresnel sky reflection (grazing views mirror the pale horizon, steep ones the blue
    // overhead) over a rippled normal, and a sun glitter path.
    vec3 vd = normalize(cameraPosition - vW);
    float nx = noise(p * 1.7 + 11.0) - 0.5;
    float nz = noise(p * 1.7 - 5.0) - 0.5;
    vec3 nrm = normalize(vec3(nx * 0.35, 1.0, nz * 0.35));
    float fr = pow(1.0 - clamp(dot(vd, nrm), 0.0, 1.0), 4.0);
    vec3 sky = mix(uSkyHigh, uSkyLow, smoothstep(0.3, 0.95, fr));
    c = mix(c, sky, clamp(fr * 0.6 + 0.04, 0.0, 0.5));
    vec3 rf = reflect(-vd, nrm);
    float spec = pow(max(dot(rf, uSunDir), 0.0), 160.0);
    c += vec3(1.0, 0.94, 0.8) * spec * 2.2;
    // Foam where it meets the shore.
    float foam = (1.0 - smoothstep(0.03, 0.22 + 0.08 * n, d)) * (0.65 + 0.35 * sin(uTime * 1.6 + n * 6.0));
    c = mix(c, uFoam, clamp(foam, 0.0, 1.0) * 0.85);
    float a = mix(0.55, 0.9, smoothstep(0.0, 1.4, d));
    a = max(a, max(foam * 0.9, fr * 0.7));
    float wd = length(vW - cameraPosition);
    float fd = max(pow(smoothstep(uFogNear, uFogFar, wd), uFogShape.y) * uFogShape.x, smoothstep(285.0, 385.0, wd));
    c = mix(c, uFogColor, fd);
    gl_FragColor = vec4(c, a);
  }`;

const FALL_FRAG = `uniform float uTime; varying float vDepth; varying vec3 vW; varying vec2 vUv;
  ${NOISE_GLSL}
  void main(){
    float streak = noise(vec2(vUv.x * 14.0, vUv.y * 2.0 + uTime * 2.2));
    float s2 = noise(vec2(vUv.x * 30.0 + 5.0, vUv.y * 4.0 + uTime * 3.4));
    vec3 c = mix(vec3(0.42, 0.72, 0.86), vec3(0.92, 0.98, 1.0), smoothstep(0.45, 0.75, streak * 0.6 + s2 * 0.4));
    float edge = smoothstep(0.0, 0.12, vUv.x) * smoothstep(1.0, 0.88, vUv.x);
    float foot = smoothstep(0.12, 0.0, vUv.y);
    c = mix(c, vec3(1.0), foot * 0.7);
    gl_FragColor = vec4(c, (0.72 + 0.2 * streak) * edge);
  }`;
const FALL_VERT = `varying float vDepth; varying vec3 vW; varying vec2 vUv;
  void main(){ vUv = uv; vDepth = 1.0; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;

/** +scenery: what the water reflects (linear colours) and the sun direction (three.js space). */
export interface WaterSky {
  low: THREE.Color;
  high: THREE.Color;
  sun: THREE.Vector3;
}

function waterMaterial(flow: number, sky: WaterSky): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: U.uTime,
      uFogColor: U.uFogColor,
      uFogNear: U.uFogNear,
      uFogFar: U.uFogFar,
      uFogShape: U.uFogShape,
      uSkyLow: { value: sky.low },
      uSkyHigh: { value: sky.high },
      uSunDir: { value: sky.sun },
      uShallow: { value: new THREE.Color(0x6fd2cf) },
      uDeep: { value: new THREE.Color(0x1f5f9a) },
      uFoam: { value: new THREE.Color(0xf2fbff) },
      uFlow: { value: flow },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
  });
}

/**
 * Build the water: `heightAt` is the terrain (yd), `cell` the sampling step. The lake sheet covers
 * the basin's cells that lie below the water level; the river ribbon sits a little below its banks
 * all the way down (its surface follows the bed).
 */
export function buildWater(w: Water, heightAt: (x: number, y: number) => number, cell = 1.25, sky: WaterSky = { low: new THREE.Color(0xc8e8f6), high: new THREE.Color(0x58a4ee), sun: new THREE.Vector3(-0.55, 1.0, 0.62).normalize() }): WaterView {
  const group = new THREE.Group();
  group.name = 'water';
  const owned: { dispose(): void }[] = [];

  // ---- the lake: a grid over the ellipse's bounds, only cells with water above ground ----
  {
    const { c, rx, ry } = w.lake;
    const x0 = c.x - rx - 4;
    const y0 = c.y - ry - 4;
    const nx = Math.ceil((rx * 2 + 8) / cell) + 1;
    const ny = Math.ceil((ry * 2 + 8) / cell) + 1;
    const depth = new Float32Array(nx * ny);
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) depth[j * nx + i] = w.level - heightAt(x0 + i * cell, y0 + j * cell);
    const pos: number[] = [];
    const dep: number[] = [];
    const idx: number[] = [];
    const vid = new Map<number, number>();
    const v = (k: number): number => {
      let id = vid.get(k);
      if (id !== undefined) return id;
      id = pos.length / 3;
      vid.set(k, id);
      pos.push(x0 + (k % nx) * cell, w.level, -(y0 + Math.floor(k / nx) * cell));
      dep.push(depth[k]!);
      return id;
    };
    for (let j = 0; j < ny - 1; j++)
      for (let i = 0; i < nx - 1; i++) {
        const a = j * nx + i;
        const b = a + 1;
        const cc = a + nx;
        const e = cc + 1;
        if (Math.max(depth[a]!, depth[b]!, depth[cc]!, depth[e]!) <= -0.05) continue;
        idx.push(v(a), v(b), v(cc), v(b), v(e), v(cc));
      }
    if (idx.length) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('aDepth', new THREE.Float32BufferAttribute(dep, 1));
      g.setIndex(idx);
      g.computeBoundingSphere();
      const m = new THREE.Mesh(g, waterMaterial(0, sky));
      m.name = 'lake';
      m.renderOrder = 1;
      group.add(m);
      owned.push(g, m.material as THREE.Material);
    }
  }

  // ---- the river: a ribbon down the bed, below the falls ----
  let riverTop = w.level;
  {
    // From the foot of the falls (the cliff face starts ~10 yd south of the plateau lip) down to the lake.
    const footY = w.falls.y - 9.5;
    const raw = w.river.filter((p) => p.y <= w.falls.y + 0.5);
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < raw.length; i++) {
      const p = raw[i]!;
      if (p.y <= footY) pts.push(p);
      else {
        const q = raw[i + 1];
        if (q && q.y <= footY) {
          const t = (footY - p.y) / (q.y - p.y);
          pts.push({ x: p.x + (q.x - p.x) * t, y: footY });
        }
      }
    }
    const pos: number[] = [];
    const dep: number[] = [];
    const idx: number[] = [];
    // Resample every ~1 yd.
    const line: { x: number; y: number }[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y)));
      for (let k = 0; k < n; k++) line.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n });
    }
    line.push(pts[pts.length - 1]!);
    const across = 6;
    // The surface only ever steps down toward the lake.
    let prev = Infinity;
    const surfs = line.map((p) => (prev = Math.min(prev, Math.max(w.level, heightAt(p.x, p.y) + 1.45))));
    riverTop = surfs[0] ?? w.level;
    for (let i = 0; i < line.length; i++) {
      const p = line[i]!;
      const q = line[Math.min(line.length - 1, i + 1)]!;
      const o = line[Math.max(0, i - 1)]!;
      const dx = q.x - o.x;
      const dy = q.y - o.y;
      const l = Math.hypot(dx, dy) || 1;
      const nx = -dy / l;
      const ny = dx / l;
      const surf = surfs[i]!;
      for (let k = 0; k <= across; k++) {
        const t = (k / across) * 2 - 1;
        const x = p.x + nx * w.riverHalf * t;
        const y = p.y + ny * w.riverHalf * t;
        pos.push(x, surf, -y);
        dep.push(surf - heightAt(x, y));
      }
    }
    const row = across + 1;
    for (let i = 0; i < line.length - 1; i++)
      for (let k = 0; k < across; k++) {
        const a = i * row + k;
        idx.push(a, a + row, a + 1, a + 1, a + row, a + row + 1);
      }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aDepth', new THREE.Float32BufferAttribute(dep, 1));
    g.setIndex(idx);
    g.computeBoundingSphere();
    const mat = waterMaterial(1, sky);
    mat.side = THREE.DoubleSide;
    const m = new THREE.Mesh(g, mat);
    m.name = 'river';
    m.renderOrder = 1;
    group.add(m);
    owned.push(g, mat);
  }

  // ---- the falls: a sheet from the plateau's lip to the river, foam and mist at its foot ----
  {
    const f = w.falls;
    const base = riverTop - 0.2;
    const h = f.top - base + 0.4;
    const g = new THREE.PlaneGeometry(5.2, h, 1, 8);
    const p = g.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const t = (p.getY(i) + h / 2) / h;
      // Leans back into the cliff toward the lip, bowing out a little at the top.
      p.setZ(i, -t * 7.5 + t * t * 1.2);
    }
    g.translate(0, base + h / 2, 0);
    const mat = new THREE.ShaderMaterial({ uniforms: { uTime: U.uTime }, vertexShader: FALL_VERT, fragmentShader: FALL_FRAG, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    const m = new THREE.Mesh(g, mat);
    m.position.set(f.x, 0, -(f.y - 8.6));
    m.name = 'waterfall';
    m.renderOrder = 2;
    group.add(m);
    owned.push(g, mat);
    const mist = motes(40, 2.6, 3.5, [0.85, 0.95, 1.0], 0.3, 1, 1);
    mist.position.set(f.x, base, -(f.y - 10.5));
    group.add(mist);
    owned.push(mist.geometry, mist.material as THREE.Material);
  }

  return {
    group,
    dispose() {
      group.removeFromParent();
      for (const o of owned) o.dispose();
    },
  };
}
